import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CalcOption } from "@/lib/calculators/types";

interface SelectProps {
  id?: string;
  value: number;
  options: CalcOption[];
  ariaLabel?: string;
  onChange: (value: number) => void;
}

export default function Select({ id, value, options, ariaLabel, onChange }: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-[#E7ECF6] bg-white pl-3.5 pr-3 text-[14px] font-medium text-gray-900 outline-none transition-colors hover:border-crayoBlue/40 focus-visible:border-crayoBlue focus-visible:ring-2 focus-visible:ring-crayoBlue/30"
      >
        <span className="truncate">{selected?.label}</span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-[#8B93A7] transition-transform duration-200",
            open && "rotate-180",
          )}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-[#E7ECF6] bg-white p-1.5 shadow-[0_12px_32px_-8px_rgba(20,30,60,0.18)]"
        >
          {options.map((opt) => {
            const active = opt.value === selected?.value;
            return (
              <li key={opt.label} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-[13.5px] transition-colors",
                    active
                      ? "bg-crayoBlue/10 font-medium text-crayoBlue"
                      : "text-[#41485A] hover:bg-[#F4F6FB]",
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {active && <Check className="size-3.5 shrink-0" strokeWidth={2.5} aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
